import * as React from "react";
import styled, {
  css,
  useTheme
} from "styled-components";
import { lighten } from "polished";
import {
  useHistory,
  useLocation
} from "react-router-dom";

import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { Text } from "src/library/components/Text/Text";
import useList from "src/core/lists/hooks/useList";
import { calculateCartTotalQuantity } from "src/core/orders/utils/orders";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { TotalPrice } from "src/core/prices/components/TotalPrice";
import { MenuContextType } from "src/core/shared/hooks/menu/useMenu";

interface SidebarListQuickActionProps {
  context: MenuContextType;
}

export const SidebarListQuickAction: React.ReactFCWithChildren<SidebarListQuickActionProps> = () => {
  const [ { currentList: { data: list } } ] = useList();
  const pricesVisible = usePriceVisibility();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();

  if (!list) {
    return null;
  }

  const itemCount = list.listLines ? calculateCartTotalQuantity(list.listLines) : 0;
  const isOnListPage = location.pathname.startsWith(`/lists/${list.id}`);
  const destination = isOnListPage ? "/products" : `/lists/${list.id}`;
  const actionLabel = isOnListPage ? "Back to products" : "View list";

  return (
    <QuickActionWrapper
      onClick={() => history.push(destination)}
      data-cypress="sidebar-list-quick-action"
    >
      <Icon
        icon={isOnListPage ? Icons.CHEVRONLEFT : Icons.SHOPPINGBAG}
        $fillColor="white"
        size={20}
      />

      <div className="list-summary">
        <Text
          fontSize="sub"
          color={theme.colors.accentLight}
          className="action-label"
        >
          {actionLabel}
        </Text>

        <Text
          fontSize="sub"
          color={theme.overrides?.menu?.textColor ?? "white"}
          className="item-count"
        >
          {list.listLines && pricesVisible ? (
            <>
              <TotalPrice
                lines={list.listLines}
                id={list.id}
                totalOnly
                currency={list.branch?.priceCode?.currency}
              />

              {` · ${itemCount} item${itemCount !== 1 ? "s" : ""}`}
            </>
          ) : (
            `${itemCount} item${itemCount !== 1 ? "s" : ""}`
          )}
        </Text>
      </div>

      {!isOnListPage && (
        <Icon
          className="chevron"
          icon={Icons.CHEVRONRIGHT}
          $fillColor="white"
          size={20}
        />
      )}
    </QuickActionWrapper>
  );
};

const QuickActionWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing.margins.sm};
    padding: ${theme.spacing.padding.md};
    background: ${theme.colors.primaryDark};
    cursor: pointer;
    min-height: 60px;
    border-top: 1px solid ${lighten(0.05, theme.colors.primaryDark)};

    ${theme.overrides?.menu?.iconColor ? css`
      svg {
        fill: ${theme.overrides.menu.iconColor} !important;
      }
    ` : ""}

    .list-summary {
      flex: 1;
      display: flex;
      flex-direction: column;

      .action-label {
        color: ${theme.colors.accentLight};
        font-weight: ${theme.fonts.weights.bold};
        letter-spacing: 1px;
        text-transform: uppercase;
      }

      .item-count span {
        color: ${theme.overrides?.menu?.textColor ?? theme.colors.white};
      }
    }

    .chevron {
      transition: ${theme.transition?.quickest};
      transform: translateX(-4px);
    }

    &:hover {
      background: ${lighten(0.04, theme.colors.primaryDark)};

      .chevron {
        transform: translateX(0);
      }
    }
  `}
`;
